import { CompanyInfoBanner } from "@/components/CompanyInfoBanner";
import { ContractorBlock } from "@/components/ContractorBlock";
import { ReceiptDialog } from "@/components/ReceiptDialog";
import { ReceiptsList, useReceipts } from "@/components/ReceiptsList";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Handshake, Printer, WhatsappLogo, Envelope, Truck, User, Buildings, MapPin, GasPump, Receipt, HandCoins, FileText, PenNib, Eraser, FilePdf, Signature, RoadHorizon, ClockCounterClockwise, ArrowRight, MicrosoftExcelLogo } from "@phosphor-icons/react";
import { fmtDate } from "@/lib/utils";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const num = (n, d = 2) => (Number(n) || 0).toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d });

function SignaturePad({ label, testId }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * dpr;
    c.height = c.offsetHeight * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
  };

  return (
    <div className="border rounded-md bg-white">
      <canvas
        ref={canvasRef}
        data-testid={testId}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full h-32 cursor-crosshair touch-none"
      />
      <div className="flex items-center justify-between px-2 py-1 border-t border-slate-100 bg-slate-50/70 rounded-b-md">
        <div className="text-xs text-slate-600">{label}</div>
        <button type="button" onClick={clear} className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1 print:hidden">
          <Eraser size={12} /> Limpar
        </button>
      </div>
    </div>
  );
}

export default function AcertoFinal() {
  const { user } = useAuth();
  const [viagens, setViagens] = useState([]);
  const [viagemId, setViagemId] = useState("");
  const [statusFilter, setStatusFilter] = useState("todas"); // todas | aberta | finalizada
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [receiptGroup, setReceiptGroup] = useState(null);
  const [receipts, loadReceipts] = useReceipts({ viagem_id: viagemId || "__none" });
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [xlsxMonth, setXlsxMonth] = useState("all"); // all | 1-12 (paired with current year)
  const [xlsxYear, setXlsxYear] = useState(new Date().getFullYear());
  const [xlsxBusy, setXlsxBusy] = useState(false);

  const downloadXlsx = async () => {
    setXlsxBusy(true);
    try {
      const params = { year: xlsxYear };
      if (xlsxMonth !== "all") params.month = Number(xlsxMonth);
      if (statusFilter !== "todas") params.status = statusFilter;
      const res = await api.get("/acerto/export-xlsx", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const fname = xlsxMonth === "all" ? `acertos-${xlsxYear}.xlsx` : `acertos-${xlsxYear}-${String(xlsxMonth).padStart(2, "0")}.xlsx`;
      a.download = fname;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Planilha exportada ✅");
    } catch (e) {
      console.error(e);
      toast.error("Erro ao gerar planilha");
    } finally { setXlsxBusy(false); }
  };

  const loadViagens = () => {
    api.get("/acerto/list-finalized").then((r) => {
      setViagens(r.data);
      setHistory(r.data);
      if (r.data.length > 0 && !viagemId) setViagemId(r.data[0].id);
    });
  };

  useEffect(() => { loadViagens(); }, []); // eslint-disable-line
  useEffect(() => {
    const h = () => loadViagens();
    window.addEventListener("live-event", h);
    return () => window.removeEventListener("live-event", h);
  }, []); // eslint-disable-line

  const filteredViagens = useMemo(() => {
    if (statusFilter === "todas") return viagens;
    return viagens.filter((v) => v.status === statusFilter);
  }, [viagens, statusFilter]);

  // When the filter switches, ensure the selected viagem is still valid
  useEffect(() => {
    if (filteredViagens.length && !filteredViagens.find((v) => v.id === viagemId)) {
      setViagemId(filteredViagens[0].id);
    }
    // eslint-disable-next-line
  }, [statusFilter]);

  useEffect(() => {
    if (!viagemId) { setData(null); return; }
    setLoading(true);
    api.get(`/acerto/viagem/${viagemId}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Erro ao carregar acerto"))
      .finally(() => setLoading(false));
  }, [viagemId, reloadKey]);

  const saldo = data?.prestacao_contas?.saldo ?? data?.acerto_motorista?.saldo ?? 0;
  const saldoLabel = saldo > 0 ? "Empresa paga ao motorista" : saldo < 0 ? "Motorista paga à empresa" : "Zerado";
  const saldoColor = saldo > 0 ? "text-emerald-700" : saldo < 0 ? "text-red-700" : "text-slate-700";

  const rotas = useMemo(() => {
    if (!data?.fretes?.length) return { origem: "-", destino: "-" };
    const first = data.fretes[0], last = data.fretes[data.fretes.length - 1];
    const composeO = [first.origin, first.origin_uf].filter(Boolean).join("/");
    const composeD = [last.destination, last.destination_uf].filter(Boolean).join("/");
    return { origem: composeO || "-", destino: composeD || "-" };
  }, [data]);

  const token = localStorage.getItem("token");

  const [genPdf, setGenPdf] = useState(false);
  const [sharing, setSharing] = useState(false);

  // Generate PDF as jsPDF instance (does NOT save/download); returns { pdf, blob, filename }
  const buildPdf = async () => {
    const node = document.getElementById("acerto-printable");
    if (!node || !data) return null;
    const canvas = await html2canvas(node, {
      scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false, windowWidth: node.scrollWidth,
    });
    const imgData = canvas.toDataURL("image/jpeg", 0.92);
    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pdfW = pdf.internal.pageSize.getWidth();
    const pdfH = pdf.internal.pageSize.getHeight();
    const imgW = pdfW;
    const imgH = (canvas.height * imgW) / canvas.width;
    let heightLeft = imgH;
    let position = 0;
    pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
    heightLeft -= pdfH;
    while (heightLeft > 0) {
      position = heightLeft - imgH;
      pdf.addPage();
      pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
      heightLeft -= pdfH;
    }
    const filename = `Acerto-Viagem-${data.viagem?.viagem_number || "x"}-${(data.driver?.name || "motorista").replace(/\s+/g, "_")}.pdf`;
    const blob = pdf.output("blob");
    return { pdf, blob, filename };
  };

  const downloadPDF = async () => {
    if (!data) return;
    setGenPdf(true);
    try {
      const built = await buildPdf();
      if (!built) return;
      built.pdf.save(built.filename);
      toast.success("PDF gerado ✅");
    } catch (e) {
      toast.error("Erro ao gerar PDF");
      console.error(e);
    } finally { setGenPdf(false); }
  };

  const shareWithPdf = async (channel) => {
    if (!data) return;
    setSharing(true);
    try {
      const built = await buildPdf();
      if (!built) return;
      // Try native Web Share (Android/iOS) with the file attached
      try {
        const shareFile = new File([built.blob], built.filename, { type: "application/pdf" });
        if (channel === "native" && navigator.canShare && navigator.canShare({ files: [shareFile] })) {
          await navigator.share({
            title: `Acerto Viagem ${data.viagem?.viagem_number}`,
            text: `Acerto Final da Viagem ${data.viagem?.viagem_number} — ${data.driver?.name || ""}`,
            files: [shareFile],
          });
          setSharing(false);
          return;
        }
      } catch {}

      // Fallback: upload to backend and use public share URL
      const fd = new FormData();
      fd.append("file", built.blob, built.filename);
      const up = await api.post(`/acerto/${data.viagem.id}/share-pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const publicUrl = `${API}/public/acerto/${up.data.share_token}`;

      const lines = [
        `*Acerto Final — Viagem ${data.viagem?.viagem_number}*`,
        ...(data.contractor ? [`Contratante: ${data.contractor.name}${data.contractor.cnpj ? ` (CNPJ ${data.contractor.cnpj})` : ""}`] : []),
        `${user?.role === "autonomo" ? "Prestador" : "Empresa"}: ${data.company?.name || "-"}`,
        `Motorista: ${data.driver?.name || "-"}`,
        `Rota: ${rotas.origem} → ${rotas.destino}`,
        `KM: ${num(data.km_total, 0)}  ·  Diesel: ${num(data.diesel_liters)} L  ·  Média: ${num(data.avg_km_per_liter)} km/L`,
        `Bruto: ${fmt(data.gross_freight)}  ·  Líquido: ${fmt(data.net_freight)}  ·  Comissão: ${fmt(data.commission)}`,
        `Saldo: ${fmt(saldo)} (${saldoLabel})`,
        "",
        `📄 PDF completo: ${publicUrl}`,
        "",
        `Gerado no Acerto Rápido JVO`,
      ].join("\n");

      if (channel === "whatsapp") {
        window.open(`https://wa.me/?text=${encodeURIComponent(lines)}`, "_blank");
        toast.success("PDF pronto! Link incluído na mensagem do WhatsApp");
      } else if (channel === "email") {
        const subject = encodeURIComponent(`Acerto Final — Viagem ${data.viagem?.viagem_number}`);
        const body = encodeURIComponent(lines);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
        toast.success("PDF pronto! Link incluído no e-mail");
      } else if (channel === "copy") {
        try { await navigator.clipboard.writeText(publicUrl); toast.success("Link do PDF copiado"); } catch {}
      }
    } catch (e) {
      console.error(e);
      toast.error("Erro ao gerar/compartilhar PDF");
    } finally { setSharing(false); }
  };

  const canhotos = (data?.documents || []).filter((d) => d.label === "canhoto");
  const pedagios = (data?.documents || []).filter((d) => d.label === "vale_pedagio");
  const outrosDocs = (data?.documents || []).filter((d) => d.label !== "canhoto" && d.label !== "vale_pedagio");
  const fretesById = useMemo(() => Object.fromEntries((data?.fretes || []).map((f) => [f.id, f])), [data]);
  const docSubtitle = (d) => {
    const f = fretesById[d.trip_id];
    if (!f) return "";
    return `F${f.frete_number} · ${f.origin || "?"} → ${f.destination || "?"}`;
  };

  return (
    <div className="space-y-6 print:space-y-3">
      <CompanyInfoBanner />
      <div className="flex items-center justify-between gap-4 flex-wrap print:hidden">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
            <Handshake size={32} weight="duotone" className="text-blue-600" /> Acerto Final
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Consolidação da viagem — pronto pra imprimir, WhatsApp ou e-mail.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={viagemId} onValueChange={setViagemId}>
            <SelectTrigger data-testid="acerto-viagem-select" className="w-[240px]"><SelectValue placeholder="Selecionar viagem" /></SelectTrigger>
            <SelectContent>
              {filteredViagens.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">Nenhuma viagem nesse filtro</div>}
              {filteredViagens.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  Viagem {v.viagem_number} · {v.origin || "?"}→{v.destination || "?"} · {v.status === "aberta" ? "Em andamento" : "Finalizada"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => window.print()} data-testid="btn-print-acerto" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white gap-1.5"><Printer size={16} weight="duotone" /> Imprimir</Button>
          <Button onClick={downloadPDF} disabled={genPdf || sharing} data-testid="btn-download-pdf" className="bg-red-600 hover:bg-red-700 hover:text-white text-white gap-1.5"><FilePdf size={16} weight="fill" /> {genPdf ? "Gerando…" : "Baixar PDF"}</Button>
          <Button variant="outline" onClick={() => shareWithPdf("whatsapp")} disabled={sharing || genPdf} data-testid="btn-share-whatsapp" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1.5"><WhatsappLogo size={16} weight="fill" /> {sharing ? "Enviando…" : "WhatsApp"}</Button>
          <Button variant="outline" onClick={() => shareWithPdf("email")} disabled={sharing || genPdf} data-testid="btn-share-email" className="gap-1.5"><Envelope size={16} weight="duotone" /> E-mail</Button>
        </div>
      </div>

      {/* Status filter + Histórico toggle */}
      <div className="flex items-center gap-3 flex-wrap print:hidden">
        <div className="inline-flex rounded-lg border bg-white p-0.5 text-sm">
          {[
            { v: "todas", l: "Todas", n: viagens.length },
            { v: "aberta", l: "Em andamento", n: viagens.filter((x) => x.status === "aberta").length },
            { v: "finalizada", l: "Finalizadas", n: viagens.filter((x) => x.status === "finalizada").length },
          ].map((opt) => (
            <button
              key={opt.v}
              data-testid={`filter-${opt.v}`}
              onClick={() => setStatusFilter(opt.v)}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${statusFilter === opt.v ? "bg-blue-600 text-white" : "text-slate-600 hover:text-blue-700"}`}
            >
              {opt.l} <span className="ml-1 text-xs opacity-70">({opt.n})</span>
            </button>
          ))}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setHistoryOpen((o) => !o)}
          data-testid="btn-toggle-history"
          className="gap-1.5 text-blue-700 hover:bg-blue-50"
        >
          <ClockCounterClockwise size={16} weight="duotone" /> {historyOpen ? "Ocultar" : "Ver"} histórico
        </Button>
      </div>

      {historyOpen && (
        <Card data-testid="history-card" className="print:hidden">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <ClockCounterClockwise size={18} weight="duotone" className="text-blue-600" /> Histórico de Acertos ({history.length})
                </CardTitle>
                <p className="text-xs text-slate-500">Clique em qualquer viagem para carregar o acerto — pronto pra reimprimir ou reenviar.</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={String(xlsxMonth)} onValueChange={(v) => setXlsxMonth(v === "all" ? "all" : Number(v))}>
                  <SelectTrigger data-testid="xlsx-month-select" className="w-[140px] h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os meses</SelectItem>
                    {["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"].map((n, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(xlsxYear)} onValueChange={(v) => setXlsxYear(Number(v))}>
                  <SelectTrigger data-testid="xlsx-year-select" className="w-[100px] h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[0, 1, 2, 3].map((offset) => {
                      const y = new Date().getFullYear() - offset;
                      return <SelectItem key={y} value={String(y)}>{y}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
                <Button
                  onClick={downloadXlsx}
                  disabled={xlsxBusy}
                  data-testid="btn-download-xlsx"
                  className="bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white gap-1.5 h-9"
                >
                  <MicrosoftExcelLogo size={16} weight="fill" /> {xlsxBusy ? "Gerando…" : "Baixar Excel"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Nenhuma viagem ainda.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-slate-50/60">
                      <th className="text-left py-2 px-2">Viagem</th>
                      <th className="text-left py-2 px-2">Status</th>
                      <th className="text-left py-2 px-2">Motorista</th>
                      <th className="text-left py-2 px-2">Veículo</th>
                      <th className="text-left py-2 px-2">Rota</th>
                      <th className="text-right py-2 px-2">Fretes</th>
                      <th className="text-right py-2 px-2">KM</th>
                      <th className="text-right py-2 px-2">Bruto</th>
                      <th className="text-right py-2 px-2">Líquido</th>
                      <th className="text-right py-2 px-2">Comissão</th>
                      <th className="text-right py-2 px-2">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.id} className={`border-b hover:bg-blue-50/40 ${h.id === viagemId ? "bg-blue-50/60" : ""}`} data-testid={`history-row-${h.id}`}>
                        <td className="py-1.5 px-2 font-bold">V{h.viagem_number}</td>
                        <td className="py-1.5 px-2">
                          <Badge variant="outline" className={h.status === "finalizada" ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-amber-300 text-amber-700 bg-amber-50"}>
                            {h.status === "finalizada" ? "Finalizada" : "Em andamento"}
                          </Badge>
                        </td>
                        <td className="py-1.5 px-2">{h.driver_name}</td>
                        <td className="py-1.5 px-2">{h.plate_truck}{h.model && <span className="text-slate-400 ml-1">· {h.model}</span>}</td>
                        <td className="py-1.5 px-2">{h.origin}{h.origin_uf ? "/" + h.origin_uf : ""} → {h.destination}{h.destination_uf ? "/" + h.destination_uf : ""}</td>
                        <td className="text-right py-1.5 px-2">{h.fretes_count}</td>
                        <td className="text-right py-1.5 px-2">{num(h.km_total, 0)}</td>
                        <td className="text-right py-1.5 px-2">{fmt(h.gross_freight)}</td>
                        <td className="text-right py-1.5 px-2 font-semibold text-emerald-700">{fmt(h.net_freight)}</td>
                        <td className="text-right py-1.5 px-2 text-yellow-700">{fmt(h.commission)}</td>
                        <td className="text-right py-1.5 px-2">
                          <Button size="sm" variant="ghost" onClick={() => setViagemId(h.id)} data-testid={`btn-load-acerto-${h.id}`} className="h-7 gap-1 text-blue-700 hover:bg-blue-100">
                            Abrir <ArrowRight size={12} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {loading && <p className="text-sm text-muted-foreground">Carregando acerto…</p>}

      {data && (
        <div id="acerto-printable" className="space-y-4">
          {/* Header institucional */}
          <Card className="border-2 border-blue-200 print:border-slate-400">
            <CardContent className="p-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  {data.company?.logo_file_id
                    ? <img src={`${API}/files/${data.company.logo_file_id}?auth=${localStorage.getItem("token")}`} crossOrigin="anonymous" alt="Logomarca" className="h-14 max-w-[140px] object-contain" data-testid="acerto-company-logo" />
                    : <img src="/logo-acerto.png" alt="Acerto Rápido JVO" width={56} height={56} className="rounded-lg" />}
                  <div>
                    <div className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>ACERTO FINAL DA VIAGEM</div>
                    <div className="text-xs text-slate-500">{data.company?.logo_file_id ? data.company?.name : "Documento gerado automaticamente pelo Acerto Rápido JVO"}</div>
                  </div>
                </div>
                <div className="text-right flex items-center gap-3">
                  {user?.photo_url && (
                    <img
                      src={user.photo_url}
                      alt={user.role === "company" ? "Logo da empresa" : "Foto do motorista"}
                      width={56}
                      height={56}
                      crossOrigin="anonymous"
                      className="w-14 h-14 rounded-full border-2 border-blue-300 object-cover shadow-sm"
                      data-testid="acerto-user-logo"
                    />
                  )}
                  <div>
                    <Badge className="bg-blue-600 text-white text-sm px-3 py-1" data-testid="acerto-viagem-number">Viagem Nº {data.viagem?.viagem_number}</Badge>
                    <div className="text-xs text-slate-500 mt-1">Emitido em {fmtDate(data.generated_at) || "-"}</div>
                  </div>
                </div>
              </div>

              <div className={`grid gap-4 mt-5 text-sm ${user?.role === "autonomo" ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"}`}>
                {user?.role === "autonomo" && <ContractorBlock data={data} onChanged={() => setReloadKey((k) => k + 1)} />}
                <div className="border rounded-md p-3 bg-slate-50/60">
                  <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><Buildings size={12} /> {user?.role === "autonomo" ? "Prestador" : "Empresa"}</div>
                  <div className="font-bold text-slate-900 mt-0.5">{data.company?.name || "-"}</div>
                  <div className="text-xs text-slate-600">CNPJ: {data.company?.cnpj || "-"}</div>
                  <div className="text-xs text-slate-600">{data.company?.email}</div>
                </div>
                <div className="border rounded-md p-3 bg-slate-50/60">
                  <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><User size={12} /> Motorista</div>
                  <div className="font-bold text-slate-900 mt-0.5">{data.driver?.name || "—"}</div>
                  <div className="text-xs text-slate-600">CPF: {data.driver?.cpf || "-"}</div>
                  <div className="text-xs text-slate-600">{data.driver?.phone || data.driver?.email || ""}</div>
                </div>
                <div className="border rounded-md p-3 bg-slate-50/60">
                  <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><Truck size={12} /> Veículo</div>
                  <div className="font-bold text-slate-900 mt-0.5">{data.vehicle?.plate_truck || "-"}</div>
                  <div className="text-xs text-slate-600">{data.vehicle?.model || ""}</div>
                  {data.vehicle?.plate_trailer1 && <div className="text-xs text-slate-600">Reboque: {data.vehicle.plate_trailer1}{data.vehicle.plate_trailer2 ? ` + ${data.vehicle.plate_trailer2}` : ""}</div>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Datas e Rota */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><MapPin size={16} weight="duotone" className="text-blue-600" /> Rota</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1">
                <div><span className="text-slate-500">Origem:</span> <strong>{rotas.origem}</strong></div>
                <div><span className="text-slate-500">Destino:</span> <strong>{rotas.destino}</strong></div>
                <div><span className="text-slate-500">Data saída:</span> <strong>{fmtDate(data.viagem?.date_start) || fmtDate(data.fretes?.[0]?.date_departure) || "-"}</strong></div>
                <div><span className="text-slate-500">Data carregamento:</span> <strong>{fmtDate(data.fretes?.[0]?.date_loading) || "-"}</strong></div>
                <div><span className="text-slate-500">Data encerramento:</span> <strong>{fmtDate(data.viagem?.date_end) || "-"}</strong></div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><GasPump size={16} weight="duotone" className="text-blue-600" /> KM & Consumo</CardTitle></CardHeader>
              <CardContent className="text-sm grid grid-cols-2 gap-x-3 gap-y-1">
                <div><span className="text-slate-500">KM inicial:</span> <strong>{num(data.km_start, 0)}</strong></div>
                <div><span className="text-slate-500">KM final:</span> <strong>{num(data.km_end, 0)}</strong></div>
                <div><span className="text-slate-500">KM rodado:</span> <strong className="text-blue-700">{num(data.km_total, 0)}</strong></div>
                <div><span className="text-slate-500">Fretes:</span> <strong>{data.fretes_count}</strong></div>
                <div><span className="text-slate-500">Diesel total:</span> <strong>{num(data.diesel_liters)} L</strong></div>
                <div><span className="text-slate-500">ARLA total:</span> <strong>{num(data.arla_liters)} L</strong></div>
                <div><span className="text-slate-500">Média geral:</span> <strong className={data.avg_km_per_liter > 3 ? "text-emerald-700" : data.avg_km_per_liter >= 2 ? "text-amber-700" : "text-red-700"}>{num(data.avg_km_per_liter)} km/L</strong></div>
                <div><span className="text-slate-500">Toneladas:</span> <strong>{num(data.tons, 3)} t</strong></div>
                <div className="col-span-2"><span className="text-slate-500">Preço médio/ton:</span> <strong>{fmt(data.price_per_ton_avg)}</strong></div>
              </CardContent>
            </Card>
          </div>

          {/* Fretes detalhados */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><HandCoins size={16} weight="duotone" className="text-blue-600" /> Fretes ({data.fretes_count})</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-slate-50/60">
                      <th className="text-left py-1.5 px-2">#</th>
                      <th className="text-left py-1.5 px-2">Rota</th>
                      <th className="text-left py-1.5 px-2">Cliente</th>
                      <th className="text-right py-1.5 px-2">Ton</th>
                      <th className="text-right py-1.5 px-2">R$/ton</th>
                      <th className="text-right py-1.5 px-2">Bruto</th>
                      <th className="text-right py-1.5 px-2">Descontos</th>
                      <th className="text-right py-1.5 px-2">Líquido</th>
                      <th className="text-right py-1.5 px-2">Comissão</th>
                      <th className="text-center py-1.5 px-2">Recebido por</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.fretes.map((f) => {
                      const desc = (f.discount_insurance || 0) + (f.discount_carrier_fee || 0) + (f.discount_taxes || 0) + (f.discount_others || 0);
                      return (
                        <tr key={f.id} className="border-b" data-testid={`acerto-frete-${f.id}`}>
                          <td className="py-1.5 px-2 font-semibold">V{data.viagem?.viagem_number}-F{f.frete_number}</td>
                          <td className="py-1.5 px-2">{f.origin}{f.origin_uf ? "/" + f.origin_uf : ""} → {f.destination}{f.destination_uf ? "/" + f.destination_uf : ""}</td>
                          <td className="py-1.5 px-2">{f.client || "-"}</td>
                          <td className="text-right py-1.5 px-2">{num(f.tons, 3)}</td>
                          <td className="text-right py-1.5 px-2">{fmt(f.price_per_ton)}</td>
                          <td className="text-right py-1.5 px-2">{fmt(f.total_freight)}</td>
                          <td className="text-right py-1.5 px-2 text-red-700">{fmt(desc)}</td>
                          <td className="text-right py-1.5 px-2 font-semibold text-emerald-700">{fmt(f.net_freight)}</td>
                          <td className="text-right py-1.5 px-2 text-yellow-700">{fmt(f.commission_value)}</td>
                          <td className="text-center py-1.5 px-2">
                            <Badge variant="outline" className={f.freight_received_by === "motorista" ? "border-blue-300 text-blue-700 bg-blue-50" : "border-slate-300 text-slate-700"}>
                              {f.freight_received_by === "motorista" ? "Motorista" : "Empresa"}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Descontos + Despesas + Recebimentos */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Receipt size={16} weight="duotone" className="text-blue-600" /> Descontos do Frete</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Seguros</span><span>{fmt(data.discounts.insurance)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Taxa seguradora</span><span>{fmt(data.discounts.carrier_fee)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Impostos</span><span>{fmt(data.discounts.taxes)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Outros</span><span>{fmt(data.discounts.others)}</span></div>
                <div className="flex justify-between border-t pt-1 mt-1 font-bold text-red-700"><span>Total descontos</span><span>{fmt(data.discounts.total)}</span></div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><GasPump size={16} weight="duotone" className="text-blue-600" /> Combustível & Despesas</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Diesel — Empresa pagou</span><span>{fmt(data.diesel_by_empresa)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Diesel — Motorista pagou</span><span className="text-blue-700 font-semibold">{fmt(data.diesel_by_motorista)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">ARLA — Empresa pagou</span><span>{fmt(data.arla_by_empresa)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">ARLA — Motorista pagou</span><span className="text-blue-700 font-semibold">{fmt(data.arla_by_motorista)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Outras despesas — Empresa</span><span>{fmt(data.expenses_by_empresa)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Outras despesas — Motorista</span><span className="text-blue-700 font-semibold">{fmt(data.expenses_by_motorista)}</span></div>
                {Object.keys(data.expenses_by_category).length > 0 && (
                  <div className="text-xs text-slate-500 pt-1 border-t mt-1">
                    Por categoria: {Object.entries(data.expenses_by_category).map(([k, v]) => `${k}: ${fmt(v)}`).join(" · ")}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText size={16} weight="duotone" className="text-blue-600" /> Recebimentos</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Bruto — Empresa recebeu</span><span>{fmt(data.received_by_empresa.gross)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Líquido — Empresa recebeu</span><span className="font-semibold">{fmt(data.received_by_empresa.net)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Bruto — Motorista recebeu</span><span>{fmt(data.received_by_motorista.gross)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Líquido — Motorista recebeu</span><span className="font-semibold text-blue-700">{fmt(data.received_by_motorista.net)}</span></div>
                <div className="flex justify-between border-t pt-1 mt-1"><span className="text-slate-500">Adiantamento ao motorista</span><span className="text-red-700">{fmt(data.advance_driver)}</span></div>
              </CardContent>
            </Card>
          </div>

          {/* Bonificações & Diárias detalhadas */}
          {(data.bonuses?.length || 0) > 0 && (
            <Card className="border-2 border-emerald-200 bg-emerald-50/30 print:border-slate-500" data-testid="acerto-bonuses-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-emerald-800">
                  🎁 Bonificações & Diárias — {fmt(data.bonuses_total || 0)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500 border-b">
                      <tr>
                        <th className="text-left py-1.5 px-2">Frete</th>
                        <th className="text-left py-1.5 px-2">Tipo</th>
                        <th className="text-left py-1.5 px-2">Observação</th>
                        <th className="text-left py-1.5 px-2">Pago por</th>
                        <th className="text-right py-1.5 px-2">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.bonuses.map((b, i) => (
                        <tr key={b.id || i} className="border-b border-slate-100">
                          <td className="py-1.5 px-2 font-mono text-xs">F{b.frete_number || "?"}</td>
                          <td className="py-1.5 px-2">
                            {b.type === "on_time_trip"    && "⏱️ Cumpriu horário"}
                            {b.type === "professionalism" && "🌟 Profissionalismo"}
                            {b.type === "best_avg_trip"   && "⛽ Melhor média viagem"}
                            {b.type === "best_avg_month"  && "🏆 Melhor média do mês"}
                            {b.type === "best_avg_year"   && "🥇 Melhor média do ano"}
                            {b.type === "standby_daily"   && "🕒 Diária tempo parado"}
                            {b.type === "other"           && "➕ Outro"}
                          </td>
                          <td className="py-1.5 px-2 text-xs text-slate-600">{b.description || "-"}</td>
                          <td className="py-1.5 px-2 text-xs">
                            {b.paid_by === "client_company" ? "🚚 Contratante" : "🏢 Empregador"}
                          </td>
                          <td className="py-1.5 px-2 text-right font-semibold text-emerald-800">{fmt(b.value)}</td>
                        </tr>
                      ))}
                      <tr className="font-bold border-t-2 border-emerald-300 bg-emerald-50">
                        <td colSpan={4} className="py-2 px-2 text-right">Total bonificações</td>
                        <td className="py-2 px-2 text-right text-emerald-900">{fmt(data.bonuses_total || 0)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  Pagas pelo empregador: <strong>{fmt(data.bonuses_by_own_company || 0)}</strong> · Pagas pelo contratante: <strong>{fmt(data.bonuses_by_client_company || 0)}</strong>
                </div>
              </CardContent>
            </Card>
          )}

          {(data.detentions?.length || 0) > 0 && (
            <Card className="border-2 border-amber-200 bg-amber-50/30 print:border-slate-500" data-testid="acerto-detention-card">
              <CardHeader className="pb-2"><CardTitle className="text-base text-amber-800">⏱️ Estadia cobrada — {fmt(data.detention_total)}</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1">
                {data.detentions.map((d) => (
                  <div key={d.id} className="flex justify-between gap-2 border-b border-amber-100 pb-1">
                    <span>F{d.frete_number || "?"} · {d.client || "-"} · {d.place === "carregamento" ? "Carregamento" : "Descarga"} · {num(d.hours, 1)}h parado ({num(d.billable_hours, 1)}h cobráveis × {fmt(d.rate_hour)})</span>
                    <span className="font-semibold">{fmt(d.value)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold text-amber-900 pt-1" data-testid="acerto-detention-total"><span>Líquido dos fretes + estadia</span><span>{fmt(data.net_plus_detention)}</span></div>
              </CardContent>
            </Card>
          )}

          {user?.role === "autonomo" && (data.contractors?.length || 0) > 0 && (
            <Card className="border-2 border-blue-200 print:hidden" data-html2canvas-ignore="true" data-testid="acerto-receipts-card">
              <CardHeader className="pb-2"><CardTitle className="text-base text-blue-800">🧾 Recibo de prestação de serviço</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                {data.contractors.map((g, i) => (
                  <div key={i} className="flex flex-wrap items-center justify-between gap-2 border rounded-md p-2" data-testid={`acerto-contractor-group-${i}`}>
                    <div>
                      <div className="font-semibold">{g.name || "Contratante não informada"}{g.cnpj ? ` · CNPJ ${g.cnpj}` : ""}</div>
                      <div className="text-xs text-slate-500">{g.fretes.map((f) => `F${f.frete_number || "?"}`).join(", ")} · fretes {fmt(g.net)}{g.detention ? ` + estadia ${fmt(g.detention)}` : ""} = <b>{fmt(g.total)}</b></div>
                    </div>
                    <Button size="sm" onClick={() => setReceiptGroup(g)} data-testid={`btn-receipt-${i}`} className="bg-blue-600 hover:bg-blue-700 text-white">Gerar recibo</Button>
                  </div>
                ))}
                <div className="pt-2" data-testid="acerto-receipts-history">
                  <div className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-1.5">Recibos já emitidos desta viagem</div>
                  <ReceiptsList items={receipts.items} onChanged={loadReceipts} />
                </div>
              </CardContent>
            </Card>
          )}
          <ReceiptDialog group={receiptGroup} data={data} onClose={() => setReceiptGroup(null)} onSaved={loadReceipts} />

          {/* Resumo Final — Acerto do Motorista (Prestação de Contas) */}
          <Card className="border-2 border-emerald-200 bg-emerald-50/30 print:border-slate-500">
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Handshake size={22} weight="duotone" className="text-emerald-700" /> Prestação de Contas — Motorista</CardTitle></CardHeader>
            <CardContent>
              <div className="text-xs text-slate-600 mb-4 italic">
                Todas as despesas pagas pelo motorista (independente da forma) são abatidas do dinheiro que ele já recebeu — adiantamento e/ou frete em mãos.
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                {/* 1. Recebido */}
                <div className="rounded-lg border-2 border-blue-200 bg-blue-50/60 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-blue-800 font-bold mb-2">1. Recebido pelo motorista</div>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between"><span>Adiantamentos</span><span>{fmt(data.prestacao_contas?.recebido?.adiantamento ?? data.acerto_motorista.adiantamento)}</span></div>
                    <div className="flex justify-between"><span>Frete em mãos</span><span>{fmt(data.prestacao_contas?.recebido?.frete_em_maos ?? data.acerto_motorista.fretes_recebidos)}</span></div>
                    <div className="flex justify-between border-t border-blue-300 pt-1 mt-1 font-extrabold text-blue-900" data-testid="prestacao-recebido-total">
                      <span>Total recebido</span>
                      <span>{fmt(data.prestacao_contas?.recebido?.total ?? (data.acerto_motorista.adiantamento + data.acerto_motorista.fretes_recebidos))}</span>
                    </div>
                  </div>
                </div>

                {/* 2. Gastos comprovados */}
                <div className="rounded-lg border-2 border-amber-200 bg-amber-50/60 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold mb-2">2. Gastos comprovados (abate)</div>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between"><span>Diesel pago</span><span>{fmt(data.prestacao_contas?.gastos_comprovados?.diesel ?? data.acerto_motorista.reembolso_diesel)}</span></div>
                    <div className="flex justify-between"><span>ARLA pago</span><span>{fmt(data.prestacao_contas?.gastos_comprovados?.arla ?? data.acerto_motorista.reembolso_arla)}</span></div>
                    <div className="flex justify-between"><span>Outras despesas</span><span>{fmt(data.prestacao_contas?.gastos_comprovados?.outras_despesas ?? data.acerto_motorista.reembolso_despesas)}</span></div>
                    {(data.prestacao_contas?.gastos_comprovados?.despesas_frete ?? data.acerto_motorista.despesas_caminhao) > 0 && (
                      <div className="flex justify-between"><span>Descontos do frete</span><span>{fmt(data.prestacao_contas?.gastos_comprovados?.despesas_frete ?? data.acerto_motorista.despesas_caminhao)}</span></div>
                    )}
                    <div className="flex justify-between border-t border-amber-300 pt-1 mt-1 font-extrabold text-amber-900" data-testid="prestacao-gastos-total">
                      <span>Total gastos</span>
                      <span>{fmt(data.prestacao_contas?.gastos_comprovados?.total ?? (data.acerto_motorista.reembolso_diesel + data.acerto_motorista.reembolso_arla + data.acerto_motorista.reembolso_despesas + (data.acerto_motorista.despesas_caminhao || 0)))}</span>
                    </div>
                  </div>
                </div>

                {/* 3. Direitos */}
                <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/60 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold mb-2">3. Direitos do motorista</div>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between"><span>Comissão</span><span>{fmt(data.prestacao_contas?.direitos?.comissao ?? data.acerto_motorista.comissao)}</span></div>
                    <div className="flex justify-between text-emerald-700" data-testid="acerto-bonuses"><span>🎁 Bonificações & Diárias</span><span className="font-semibold">{fmt(data.prestacao_contas?.direitos?.bonificacoes ?? data.acerto_motorista.bonificacoes ?? 0)}</span></div>
                    <div className="flex justify-between border-t border-emerald-300 pt-1 mt-1 font-extrabold text-emerald-900" data-testid="prestacao-direitos-total">
                      <span>Total direitos</span>
                      <span>{fmt(data.prestacao_contas?.direitos?.total ?? (data.acerto_motorista.comissao + (data.acerto_motorista.bonificacoes || 0)))}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fórmula visual */}
              <div className="mt-4 rounded-md bg-white border border-slate-200 p-3 text-xs text-center text-slate-600 font-mono">
                <span className="text-emerald-700 font-bold">Direitos</span> <span>({fmt(data.prestacao_contas?.direitos?.total || 0)})</span>
                <span className="mx-1">+</span>
                <span className="text-amber-700 font-bold">Gastos</span> <span>({fmt(data.prestacao_contas?.gastos_comprovados?.total || 0)})</span>
                <span className="mx-1">−</span>
                <span className="text-blue-700 font-bold">Recebido</span> <span>({fmt(data.prestacao_contas?.recebido?.total || 0)})</span>
                <span className="mx-1">=</span>
                <span className="font-extrabold text-slate-900">Saldo</span>
              </div>

              <div className="mt-4 border-t-2 border-dashed border-emerald-300 pt-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div className="text-sm text-slate-700 font-semibold">SALDO FINAL DO ACERTO:</div>
                  <div className="text-right">
                    <div className={`text-3xl font-extrabold ${saldoColor}`} data-testid="acerto-saldo" style={{ fontFamily: "Manrope" }}>{fmt(Math.abs(saldo))}</div>
                    <div className={`text-xs font-bold uppercase ${saldoColor}`}>{saldoLabel}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Provas de Entrega + Pedágios */}
          {(canhotos.length > 0 || pedagios.length > 0 || outrosDocs.length > 0) && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText size={16} weight="duotone" className="text-blue-600" /> Comprovantes anexados</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {canhotos.length > 0 && (
                  <div data-testid="acerto-canhotos-section">
                    <div className="text-xs uppercase tracking-wider font-bold text-blue-800 flex items-center gap-1.5 mb-2">
                      <Signature size={14} weight="fill" /> Canhotos / CTe assinados ({canhotos.length}) — Prova de entrega
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                      {canhotos.map((d) => (
                        <a key={d.id} href={`${API}/files/${d.file_id}?auth=${token}`} target="_blank" rel="noreferrer" className="block group">
                          <div className="aspect-square rounded-md border-2 border-blue-300 overflow-hidden bg-white">
                            <img crossOrigin="anonymous" src={`${API}/files/${d.file_id}?auth=${token}`} alt="canhoto" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          </div>
                          <div className="text-[10px] text-slate-600 text-center mt-1 truncate">{docSubtitle(d)}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {pedagios.length > 0 && (
                  <div data-testid="acerto-pedagios-section">
                    <div className="text-xs uppercase tracking-wider font-bold text-amber-800 flex items-center gap-1.5 mb-2">
                      <RoadHorizon size={14} weight="fill" /> Vale-Pedágio / Recibos ({pedagios.length})
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                      {pedagios.map((d) => (
                        <a key={d.id} href={`${API}/files/${d.file_id}?auth=${token}`} target="_blank" rel="noreferrer" className="block group">
                          <div className="aspect-square rounded-md border-2 border-amber-300 overflow-hidden bg-white">
                            <img crossOrigin="anonymous" src={`${API}/files/${d.file_id}?auth=${token}`} alt="pedagio" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          </div>
                          <div className="text-[10px] text-slate-600 text-center mt-1 truncate">{docSubtitle(d)}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {outrosDocs.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wider font-bold text-slate-700 flex items-center gap-1.5 mb-2">
                      <FileText size={14} /> Outros documentos ({outrosDocs.length})
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                      {outrosDocs.map((d) => (
                        <a key={d.id} href={`${API}/files/${d.file_id}?auth=${token}`} target="_blank" rel="noreferrer" className="block group">
                          <div className="aspect-square rounded-md border border-slate-300 overflow-hidden bg-white">
                            <img crossOrigin="anonymous" src={`${API}/files/${d.file_id}?auth=${token}`} alt={d.label} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          </div>
                          <div className="text-[10px] text-slate-600 text-center mt-1 truncate">{d.label} · {docSubtitle(d)}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Assinaturas */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><PenNib size={16} weight="duotone" className="text-blue-600" /> Assinaturas</CardTitle></CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Responsável pelo Acerto (Empresa)</div>
                  <SignaturePad label="Assinatura do responsável" testId="sig-company" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Motorista</div>
                  <SignaturePad label={data.driver?.name || "Assinatura do motorista"} testId="sig-driver" />
                </div>
              </div>
              <p className="text-[10px] text-slate-500 mt-3 print:block">
                As partes reconhecem que os valores acima refletem fielmente as despesas, adiantamentos e receitas da viagem Nº {data.viagem?.viagem_number}, gerado eletronicamente pelo aplicativo Acerto Rápido JVO em {fmtDate(data.generated_at)}.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <style>{`
        @media print {
          body { background: #fff !important; }
          nav, header, .print\\:hidden { display: none !important; }
          #acerto-printable { padding: 0 !important; }
          .shadow, .shadow-md, .shadow-sm { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
